<template>
    <div :id="compModalIdentifier" class="modal" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered modal-xl">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title text-center">
                        {{ hasDeliveryNote ? 'Edit' : 'Create' }} Delivery Note
                    </h5>
                    <button aria-label="Close" class="btn-close" data-bs-dismiss="modal" type="button"></button>
                </div>
                <div class="modal-body bg-light">
                    <fieldset>
                        <div class="row">
                            <div class="col-12">
                                <AdminBlock title="Details">
                                    <p class="mb-0">
                                        <strong>
                                            Job:
                                        </strong>
                                        <a :href="getJobLink()">
                                            {{ job.reference }}
                                        </a>
                                    </p>
                                    <p class="mb-0">
                                        <strong>
                                            Customer:
                                        </strong>
                                        <a :href="getCustomerLink()">
                                            {{ job.customer.name }}
                                        </a>
                                    </p>
                                    <p class="mb-0">
                                        <strong>
                                            Date of Movement:
                                        </strong>
                                        <span v-html="movementDate"></span>
                                    </p>
                                    <p class="mb-0">
                                        <strong>
                                            Movement:
                                        </strong>
                                        <a :href="getMovementLink()">
                                            {{ movement.journey }}
                                        </a>
                                    </p>
                                    <p class="mb-0">
                                        <strong>Driver:</strong>
                                        <template v-if="movement.driver">
                                            <a :href="getDriverLink()">{{ movement.driver.name }}</a>
                                        </template>
                                        <template v-else>
                                            No Driver Assigned
                                        </template>
                                    </p>

                                    <p class="mb-0">
                                        <strong>
                                            Vehicle Reg:
                                        </strong>
                                        <template v-if="job?.vehicle">
                                            <a :href="getVehicleLink()">
                                                {{ job?.vehicle?.reg ?? '' }}
                                            </a>
                                            {{ job.vehicle.description }}
                                            <span style="white-space:nowrap">( {{
                                                    job?.vehicle?.vehicle_type?.type ?? ''
                                                }} )</span>
                                        </template>
                                        <template v-else>
                                            No Vehicle Assigned
                                        </template>
                                    </p>
                                </AdminBlock>
                            </div>
                            <div class="col-12">
                                <div class="row">
                                    <div class="col-6">
                                        <AdminBlock title="Upload" :remove-margin="!hasDeliveryNote">
                                            <p class="mb-4">
                                                Please upload your Delivery Note file here
                                            </p>

                                            <FileUpload
                                                :file-upload-route="getFileUploadRoute()"
                                                ref="fileUpload"
                                                multiple
                                                no-label
                                                no-margin
                                                @file-uploaded="handleFileUploaded"
                                            />
                                        </AdminBlock>
                                    </div>
                                    <div class="col-6">
                                        <AdminBlock title="Explanation" :remove-margin="!hasDeliveryNote">
                                            <p class="mb-4">
                                                What is this Delivery Note for?
                                            </p>

                                            <InputTextField
                                                v-model="reference"
                                                label=""
                                                name="reference"
                                                placeholder="Reference"
                                                remove-margin
                                            />
                                        </AdminBlock>
                                    </div>
                                </div>
                            </div>

                            <template v-if="movement?.delivery_note?.files.length > 0">
                                <div class="col-12">
                                    <AdminBlock class="mb-0" :title="'File' + (movement.delivery_note.files.length > 1 ? 's' : '')">
                                        <table class="table table-striped mb-0">
                                            <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th style="width: 115px;">Date</th>
                                                <th style="width: 150px;">Action</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-for="file in movement.delivery_note.files" :key="file.id">
                                                <td class="align-middle">
                                                    <a :href="getFileLink(file)" class="d-flex align-items-center"
                                                       target="_blank">
                                                        <i class="fa fa-file me-2"></i>
                                                        {{ file.name }}
                                                    </a>
                                                </td>
                                                <td class="align-middle">
                                                    {{ new Date(file.created_at).toLocaleDateString() }}
                                                </td>
                                                <td class="align-middle">
                                                    <a :href="getFileLink(file)" class="btn btn-secondary btn-sm me-2" target="_blank">
                                                        View
                                                    </a>
                                                    <button class="btn btn-danger btn-sm" type="button"
                                                            @click="deleteFile(file)">
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </AdminBlock>
                                </div>
                            </template>
                        </div>
                    </fieldset>
                </div>

                <div class="modal-footer">
                    <button :id="'create_' + getModalType() + 'contact_close_button'"
                            class="btn btn-secondary"
                            data-bs-dismiss="modal"
                            type="button">Close
                    </button>

                    <button class="btn btn-primary" type="button" @click="createNewDeliveryNote">
                        {{ hasDeliveryNote ? 'Update' : 'Save' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>

import axios from 'axios';
import Modal from 'bootstrap/js/dist/modal';
import { successAlert, errorAlert } from "@/utils";
import { ref, computed, onMounted, watch } from 'vue';
import AdminBlock from '@/components/movements/components/AdminBlock.vue';
import FileUpload from "@/components/movements/components/FileUpload.vue";
import InputTextField from '@/components/movements/components/InputTextField.vue';

const props = defineProps({
    identifier: {
        type: String,
        default: '',
        required: false,
    },
    movement: {
        type: Object,
        default: () => {
        },
        required: false,
    },
    type: {
        type: String,
        default: '',
        required: false,
    },
    movementDate: {
        type: String,
        default: '',
        required: false,
    },
    modalFor: {
        type: String,
        default: '',
        required: false,
    }
});

const emit = defineEmits([
    'delivery-note-created',
    'file-deleted',
]);

const movement = ref(props.movement);
const job = ref(movement.value.job);
const deliveryNote = ref(movement.value?.delivery_note);
const reference = ref(deliveryNote.value?.reference ?? '');

watch(() => props.movement, m => {
    movement.value = m || {};
    job.value      = m?.job || {};
    reference.value = m?.delivery_note?.reference ?? '';
});


const fileInternalSignatures = ref([]);

const modal = ref({});
const files = ref([]);

const fileUpload = ref(null);

const hasDeliveryNote = computed(() => !!movement.value?.delivery_note);

const getModalType = () => {
    return props.modalFor !== '' ? props.modalFor + '_' : '';
}

const createNewDeliveryNote = (e) => {

    if (fileInternalSignatures.value.length === 0) {
        if (!confirm('Do you want to continue without adding photo evidence?')) {
            e.preventDefault();
            return;
        }
    }

    const data = {
        movement_id: props.movement.id,
        reference: reference.value,
        file_internal_signature: fileInternalSignatures.value || null,
    };

    if (hasDeliveryNote.value) {
        data.delivery_note_id = deliveryNote.id;
    }

    const [method, url] = hasDeliveryNote.value
        ? ['put', route('api.movements.delivery-notes.update', {delivery_note: movement.value?.delivery_note?.id})]
        : ['post', route('api.movements.delivery-notes.store')];

    axios[method](url, data, {
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    })
        .then(response => {
            emit('delivery-note-created', response.data);

            fileUpload.value.reset();
            fileInternalSignatures.value = [];
            files.value = [];

            successAlert('Delivery Note Created', 'Success');

            window.jQuery('.modal-header .btn-close').click();
        })
        .catch(error => {
            errorAlert(error, "Error Creating Delivery Note");
        });
}

const deleteFile = (file) => {

    if (!confirm('Are you sure you want to delete this file?')) {
        return;
    }

    axios.delete(route('api.movements.delivery-notes.files.destroy', {file: file.id}))
        .then((response) => {

            successAlert('File Deleted', 'Success');

            emit('file-deleted', response.data);

        })
        .catch((error) => {
            errorAlert(error, "Error Deleting File");
        });
}

const getFileUploadRoute = () => {
    return route('api.movements.delivery-note.getUploadUrl');
}

const getJobLink = () => {
    return route('movements.jobs.show', {job: job.value?.id});
}

const getCustomerLink = () => {
    return route('movements.customers.show', {customer: job.value?.customer.id});
}

const getMovementLink = () => {
    return route('movements.movements.show', {movement: props.movement.id});
}

const getDriverLink = () => {
    if (!props.movement?.driver) {
        return '';
    }
    return route('movements.drivers.show', {driver: props.movement.driver.id});
}

const getVehicleLink = () => {
    if (!job.value?.vehicle) {
        return '';
    }
    return route('movements.vehicles.show', {vehicle: job.value?.vehicle?.id});
}

const getFileLink = (file) => {
    if (!file) {
        return '';
    }
    return route('movements.delivery-notes.files.show', {file: file.id});
}

const handleFileUploaded = (signatures) => {
    fileInternalSignatures.value = signatures;
}

const compModalIdentifier = computed(() => {
    return props.identifier + '_delivery_note_modal';
});

onMounted(() => {
    modal.value = new Modal(document.getElementById(compModalIdentifier.value));
});
</script>
